import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	useDeleteEnquiryRecordMutation,
	useGetEnquiryRecordDetailQuery,
} from '@/api/forms-management/enquiryRecord';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import { showLabel } from '@/utils/helper';
import {
	AGE_RANGE_LIST,
	AGENCY_LIST,
	CONSENT_OBTAINED_LIST,
	RESULT_CAN_CONTACTED_TARGET_LIST,
	ENQUIRY_METHOD_LIST,
	LANGUAGE_LIST,
	PREFIX_LIST,
	SEX_LIST,
} from '@/common/constants/common';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	useGetEnquiryRecordGenerateReportMutation,
	useGetExportCustomerPDFMutation,
} from '@/api/common';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function EnquiryRecordDetailPage() {
	const { t } = useTranslation();
	const showModal = useModal();
	const { preAdmissionId, enquiryRecordId, admissionRequestId } = useParams();
	const [deleteEnquiryRecord] = useDeleteEnquiryRecordMutation();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getEnquiryRecordGenerateReport] =
		useGetEnquiryRecordGenerateReportMutation();

	const [clientInformationExpanded, setClientInformationExpanded] =
		useState(true);
	const [referrerInformationExpanded, setReferrerInformationExpanded] =
		useState(true);
	const [caseInformationExpanded, setCaseInformationExpanded] = useState(true);
	const [familyInformationExpanded, setFamilyInformationExpanded] =
		useState(true);
	const [healthInformationExpanded, setHealthInformationExpanded] =
		useState(true);
	const [resultInformationExpanded, setResultInformationExpanded] =
		useState(true);

	const { data: details }: any = useGetEnquiryRecordDetailQuery(
		enquiryRecordId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.enquiryRecord'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteEnquiryRecord(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.enquiryRecordDetails')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getEnquiryRecordGenerateReport(
									details?.data.id
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.enquiryDate')}
								value={
									details?.data.enquiryDate
										? dayjs(details?.data.enquiryDate)
												.locale('en')
												.format(DISPLAY_DATE_FORMAT)
										: ''
								}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.enquiryMethod')}
								value={showLabel(
									details?.data.enquiryMethod,
									ENQUIRY_METHOD_LIST
								)}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.creator')}
								value={details?.data.actionedBy}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setClientInformationExpanded(!clientInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.clientInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: clientInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={clientInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_zh')}
									value={details?.data.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfBirth')}
									value={
										details?.data.dateOfBirth
											? dayjs(details?.data.dateOfBirth)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.currentAge')}
									value={details?.data.currentAge}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.ageRange')}
									value={showLabel(details?.data.ageRange, AGE_RANGE_LIST)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.sex')}
									value={showLabel(details?.data.sex, SEX_LIST)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.mobilePhone')}
									value={details?.data.phone}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.maritalStatus')}
									value={details?.data.maritalStatusDesc}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.addressLanguage')}
									value={showLabel(
										details?.data.addressLanguage,
										LANGUAGE_LIST
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.region')}
									value={details?.data.regionDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.district')}
									value={details?.data.districtDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.subDistrict')}
									value={details?.data.subdistrictDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.estate/court/village')}
									value={details?.data.estateCourtVillageDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.road/street')}
									value={details?.data.roadStreet}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.road/streetNo')}
									value={details?.data.roadStreetNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.building/block')}
									value={details?.data.buildingBlockDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.building/block(specify)')}
									value={details?.data.buildingBlockSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.floor')}
									value={details?.data.floor}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.flat')}
									value={details?.data.flat}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.fullAddress')}
									value={details?.data.fullAddress}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setReferrerInformationExpanded(!referrerInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.referrerInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: referrerInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={referrerInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.approachSource')}
									value={details?.data.approachSourceDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.profileOfReferrals')}
									value={details?.data.profileOfReferralDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.profileOfReferrals(specify)')}
									value={details?.data.profileOfReferralSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referralAgency')}
									value={details?.data.referralAgencyDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referralAgency(specify)')}
									value={details?.data.referralAgencySpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.prefix')}
									value={showLabel(details?.data.prefixOfReferrer, PREFIX_LIST)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfReferrer')}
									value={details?.data.nameOfReferrer}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.positionOfReferrer')}
									value={details?.data.positionOfReferrer}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referrerContactNo')}
									value={details?.data.referrerContactNo}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.referrerFaxNo')}
									value={details?.data.referrerFaxNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.caseNatureOfTheAgency')}
									value={showLabel(
										details?.data.caseNatureOfTheAgency,
										AGENCY_LIST
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.howtheCaseKnownToReferrer')}
									value={details?.data.howTheCaseKnownToReferrer}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setCaseInformationExpanded(!caseInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.caseStatus')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: caseInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={caseInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.caseStatus')}
									valueElement={
										<>
											{details?.data.suspectedMentalHealthProblems && (
												<Typography variant='body2'>
													{t('preAdmission.suspectedMentalHealthProblems')}
												</Typography>
											)}
											{details?.data.defaultedPsychiatricFollowUp && (
												<Typography variant='body2'>
													{`${t(
														'preAdmission.defaultedPsychiatricFollowUpSince'
													)} ${
														details?.data.defaultedPsychiatricFollowUpSince
													}`}
												</Typography>
											)}
											{details?.data.awaitingPsychiatricFollowUp && (
												<Typography variant='body2'>
													{t('preAdmission.awaitingPsychiatricFollowUp')}
												</Typography>
											)}
											{details?.data.hasRegularPsychiatricFollowUp && (
												<Typography variant='body2'>
													{`${t(
														'preAdmission.hasRegularPsychiatricFollowUpSince'
													)} ${details?.data.regularPsychiatricFollowUpSince}`}
												</Typography>
											)}
										</>
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfReceivingReferral/Application')}
									value={
										details?.data.receivingReferralOrApplicationDate
											? dayjs(details?.data.receivingReferralOrApplicationDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.consentObtained')}
									value={
										details?.data.consentObtained == null
											? ''
											: showLabel(
													details.data.consentObtained,
													CONSENT_OBTAINED_LIST
												)
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(EnquiryRecord)')}
									value={details?.data.enquiryRecordRemarks}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(MembershipApplication)')}
									value={details?.data.membershipApplicationRemarks}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(FaceSheet)')}
									value={details?.data.faceSheetRemarks}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setFamilyInformationExpanded(!familyInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.backgroundInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: familyInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={familyInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.backgroundInformation')}
									value={details?.data.backgroundInformation}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setHealthInformationExpanded(!healthInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.mentalHealthCondition')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: healthInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={healthInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.cognitiveProblem')}
									value={details?.data.cognitiveProblem}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.emotionalProblem')}
									value={details?.data.emotionalProblem}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.behavioralImpairments')}
									value={details?.data.behavioralImpairments}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.interpersonalImpairments')}
									value={details?.data.interpersonalImpairments}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.riskElements')}
									value={details?.data.riskElements}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setResultInformationExpanded(!resultInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.results')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: resultInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={resultInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.results')}
									valueElement={
										<>
											{details?.data.resultHasContacted && (
												<Typography variant='body2'>
													{t('preAdmission.resultsText1')}
												</Typography>
											)}
											{details?.data.resultCanContacted && (
												<Typography variant='body2'>
													{t('preAdmission.resultsText2Full', {
														// Records saved before the choice existed fall
														// back to the paper form's uncircled wording.
														target:
															details?.data.resultCanContactedTarget == null
																? RESULT_CAN_CONTACTED_TARGET_LIST.map(
																		(option) => option.name
																	).join(' / ')
																: showLabel(
																		details.data.resultCanContactedTarget,
																		RESULT_CAN_CONTACTED_TARGET_LIST
																	),
													})}
												</Typography>
											)}
											{details?.data.resultNeedDiscuss && (
												<Typography variant='body2'>
													{t('preAdmission.resultsText3')}
												</Typography>
											)}
											{details?.data.resultNotSuitable && (
												<Typography variant='body2'>
													{`${t('preAdmission.resultsText4')} ${
														details?.data.resultNotSuitableBecause
													}`}
												</Typography>
											)}
										</>
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.informDate')}
									value={
										details?.data.dateOfInformOic
											? dayjs(details?.data.dateOfInformOic)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.confirmationDate')}
									value={details?.data.dateOfConfirmationToReferrer}
								/>
							</Grid>
						</Grid>
					</Collapse>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
